import { linearGradientDef } from '@nivo/core'
import { ResponsiveLine } from '@nivo/line'
import moment from 'moment'
import type React from 'react'
import type { DashboardDailyPoint } from '../../../redux/services/coreApi'
import { formatMoneyCompact } from './dashboardFormat'

interface VolumeChartProps {
  data: DashboardDailyPoint[]
  /** Display currency for the volume axis + tooltip (e.g. "DKK", "EUR"). */
  currency: string
}

// Trailing-30-day trade volume, as an area line. The whole point is the silhouette of momentum, so the
// axes are kept minimal and the fill is a brand-coloured gradient that reads from across the room.
const VolumeChart: React.FC<VolumeChartProps> = ({ data, currency }) => {
  const chartData = [
    {
      id: 'volume',
      data: data.map(point => ({
        x: moment.utc(point.date).format('MMM DD'),
        y: Number(point.volume.toFixed(2)),
        orders: point.orderCount,
      })),
    },
  ]

  const step = data.length > 20 ? 4 : data.length > 10 ? 2 : 1

  return (
    <div className="h-full w-full">
      <ResponsiveLine
        data={chartData}
        margin={{ top: 16, right: 24, bottom: 36, left: 56 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
        curve="monotoneX"
        enableArea
        areaOpacity={1}
        defs={[
          linearGradientDef('volumeGradient', [
            { offset: 0, color: '#F39629', opacity: 0.55 },
            { offset: 100, color: '#F39629', opacity: 0 },
          ]),
        ]}
        fill={[{ match: '*', id: 'volumeGradient' }]}
        colors={['#F8B65C']}
        lineWidth={3}
        enablePoints={false}
        enableGridX={false}
        gridYValues={4}
        axisLeft={{
          tickSize: 0,
          tickPadding: 12,
          tickValues: 4,
          format: value => formatMoneyCompact(Number(value), currency),
        }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          format: value => {
            const idx = chartData[0].data.findIndex(d => d.x === value)
            return idx % step === 0 ? value : ''
          },
        }}
        theme={{
          textColor: 'rgba(255,255,255,0.45)',
          fontSize: 12,
          axis: { ticks: { text: { fill: 'rgba(255,255,255,0.45)' } } },
          grid: { line: { stroke: 'rgba(255,255,255,0.06)' } },
          crosshair: { line: { stroke: 'rgba(255,255,255,0.4)' } },
          tooltip: { container: { background: '#1A2128', color: '#fff', fontSize: 13 } },
        }}
        useMesh
        enableSlices={false}
        tooltip={({ point }) => (
          <div className="rounded-lg bg-[#1A2128] px-3 py-2 text-sm shadow-xl ring-1 ring-white/10">
            <div className="font-semibold text-white">
              {formatMoneyCompact(Number(point.data.y), currency)}
            </div>
            <div className="text-white/50">
              {(point.data as { orders?: number }).orders ?? 0} trades · {point.data.xFormatted}
            </div>
          </div>
        )}
      />
    </div>
  )
}

export default VolumeChart
